import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAgentSetupContext } from 'contexts/AgentSetupContext';
import { PATHS } from 'routes/routes.constants';
import { toast } from 'sonner';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';
import { useRouter } from '@polyai/common/hooks/useRouter';
import Button from '@polyai/ui/components/atoms/Button';
import Input from '@polyai/ui/components/atoms/Input';
import InputWrapper from '@polyai/ui/components/atoms/InputWrapper';

import type { AgentCreationConfig } from 'api/resources/agentCreation';
import { LOGGER_TYPE } from 'api/types';
import Helmet from 'components/molecules/Helmet';
import { useVoiceOptions } from 'hooks/api/config/useVoiceOptions';
import { useAppSelector } from 'hooks/reduxHooks';
import useAuthToken from 'hooks/useAuthToken';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { useSurveyEnabled } from 'hooks/useSurveyEnabled';
import { logToDataDog } from 'lib/ddlogHelper';
import { generateNextPath } from 'lib/routeHelper';
import { LeftPanel } from 'screens/AgentWizard/components/LeftPanel';
import ProgressSliderWrapper from 'screens/AgentWizard/components/ProgressSliderWrapper';
import { Wrapper } from 'screens/AgentWizard/components/Wrapper';
import {
  ASSETS,
  ERROR_MESSAGES,
  LANGUAGE_CODES,
  TIMING,
  WIZARD_STEPS,
} from 'screens/AgentWizard/constants';
import { usePersonalAccount, useUserName } from 'screens/AgentWizard/hooks';
import AGENT_WIZARD_PATHS from 'screens/AgentWizard/paths';
import { normalizeUrl } from 'screens/AgentWizard/utils';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import { validateExternalUrl } from 'utils/validateExternalUrl';
import {
  Actions,
  ButtonWrapper,
  ContentContainer,
  Description,
  Form,
  LoadingContainer,
  LoadingText,
  Logo,
  MobileLogo,
  RightPanel,
  ShimmerText,
  Title,
} from './Company.styled';
import { COMPANY_COPY } from './Company.uiCopy';

interface CompanyFormState {
  website: string;
  setWebsite: (value: string) => void;
  urlError: string | null;
  setUrlError: (value: string | null) => void;
  handleNext: () => Promise<void>;
  isDisabled: boolean;
}

export const useCompanyForm = (
  onSubmit: (props: AgentCreationConfig) => any,
): CompanyFormState => {
  const { token, isTokenLoading } = useAuthToken();
  const [website, setWebsite] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  const userName = useUserName();
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const personalAccountId = usePersonalAccount(accountId);

  const voices = useVoiceOptions();
  const defaultVoice = useMemo(() => {
    return (
      voices.data?.voices
        .filter(voice => voice.voice_metadata?.plg_curated)
        .sort((a, b) => {
          const aIsUs = a.voice_metadata?.accent === 'en-US';
          const bIsUs = b.voice_metadata?.accent === 'en-US';
          return Number(bIsUs) - Number(aIsUs);
        })[0] ?? voices.data?.voices.find(voice => voice.name === 'Madison')
    );
  }, [voices.data]);

  const handleNext = useCallback(async () => {
    if (!token) {
      logToDataDog(
        LOGGER_TYPE.PROJECT,
        'error',
        ERROR_MESSAGES.NO_AUTH_TOKEN,
        {},
      );
      return;
    }

    // Check if personal account ID is available
    if (!personalAccountId) {
      logToDataDog(
        LOGGER_TYPE.PROJECT,
        'error',
        'No personal account ID found',
        {},
      );
      return;
    }

    // Check if website is empty
    if (!website.trim()) {
      setUrlError(ERROR_MESSAGES.REQUIRED_FIELD);
      return;
    }

    const companyUrl = normalizeUrl(website.trim());
    const validation = validateExternalUrl(companyUrl);
    const error: string | null = validation.ok ? null : validation.error;
    if (error) {
      setUrlError(error);
      return;
    }

    // Clear any previous error and persist valid URL
    setUrlError(null);

    onSubmit({
      accountId: personalAccountId,
      company: userName,
      companyUrl,
      languageCode: LANGUAGE_CODES.EN_US,
      voiceId: defaultVoice?.id,
    });
  }, [token, website, personalAccountId, userName, defaultVoice?.id, onSubmit]);

  const isDisabled = isTokenLoading || !token || !personalAccountId;

  return {
    website,
    setWebsite,
    urlError,
    setUrlError,
    handleNext,
    isDisabled,
  };
};

interface CompanyFormContentProps extends CompanyFormState {
  renderSubmit: (props: { disabled: boolean }) => React.ReactNode;
}

export const CompanyFormContent = ({
  website,
  setWebsite,
  urlError,
  setUrlError,
  handleNext,
  isDisabled,
  renderSubmit,
}: CompanyFormContentProps) => {
  return (
    <form
      data-test-id="agent-wizard-company-form"
      onSubmit={e => {
        e.preventDefault();
        void handleNext();
      }}
    >
      <Form>
        <Title>{COMPANY_COPY.title}</Title>
        <Description>{COMPANY_COPY.description}</Description>
        <InputWrapper
          errors={urlError ? [urlError] : []}
          label={COMPANY_COPY.websiteLabel}
        >
          <Input
            data-test-id="agent-wizard-company-url-input"
            inputMode="url"
            placeholder={COMPANY_COPY.websitePlaceholder}
            value={website}
            fullWidth
            onChange={e => {
              const v = e.target.value;
              setWebsite(v);
              if (urlError) {
                setUrlError(null);
              }
            }}
          />
        </InputWrapper>
        {renderSubmit({ disabled: isDisabled })}
      </Form>
    </form>
  );
};

const AgentWizardCompany = ({
  shouldBePlgProd,
}: {
  shouldBePlgProd: boolean;
}) => {
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const isFirstLogin = useAppSelector(state => state.auth.isFirstLogin);
  const { pushUrl } = useRouter();
  const isSurveyEnabled = useSurveyEnabled();
  const isPlgProd = useIsPlgProd();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<number>(0);

  const { createWizardAgent } = useAgentSetupContext();
  const handleProjectCreated = useCallback(
    async (props: AgentCreationConfig) => {
      setIsLoading(true);
      setLoadingStep(0);

      const projectId = await createWizardAgent(props);

      if (!projectId) {
        setIsLoading(false);
        return;
      }

      if (isSurveyEnabled) {
        pushUrl(
          generateNextPath(AGENT_WIZARD_PATHS.SURVEY, {
            accountId: props.accountId,
            projectId,
          }),
        );
      } else {
        pushUrl(
          generateNextPath(AGENT_WIZARD_PATHS.VOICE, {
            accountId: props.accountId,
            projectId,
          }),
        );
      }
    },
    [createWizardAgent, isSurveyEnabled, pushUrl],
  );
  const companyForm = useCompanyForm(handleProjectCreated);

  const canGoBack = isPlgProd && !isFirstLogin;
  const backUrl = canGoBack
    ? generateNextPath(PATHS.ASSISTANTS, { accountId })
    : undefined;

  useEffect(() => {
    toast.dismiss();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const timeouts: NodeJS.Timeout[] = [];

    for (let i = 1; i < COMPANY_COPY.loadingMessages.length; i++) {
      const timeout = setTimeout(() => {
        setLoadingStep(i);
      }, i * TIMING.LOADING_STEP_INTERVAL);
      timeouts.push(timeout);
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isLoading]);

  if (isPlgProd !== shouldBePlgProd) {
    return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
  }

  return (
    <>
      <Helmet title="Agent Wizard - Company" />
      <Wrapper>
        <LeftPanel bgUrl={ASSETS.LOGIN_BG}>
          <Logo aria-label="PolyAI" />
        </LeftPanel>
        <RightPanel>
          <MobileLogo aria-label="PolyAI" />
          <ProgressSliderWrapper
            step={WIZARD_STEPS.COMPANY}
            disabled={!canGoBack}
            resetButtonRedirectUrl={backUrl}
            showResetButton
          />
          <ContentContainer>
            {isLoading ? (
              <LoadingContainer data-test-id="agent-wizard-company-loading">
                <LoadingText>
                  <ShimmerText
                    animate={{
                      backgroundPosition: ['100% 0', '-100% 0'],
                    }}
                    transition={{
                      duration: 4,
                      ease: 'linear' as const,
                      repeat: Infinity,
                    }}
                  >
                    {COMPANY_COPY.loadingMessages[loadingStep] ??
                      COMPANY_COPY.fallbackMessage}
                  </ShimmerText>
                </LoadingText>
              </LoadingContainer>
            ) : (
              <CompanyFormContent
                {...companyForm}
                renderSubmit={({ disabled }) => (
                  <>
                    <ButtonWrapper>
                      <Button
                        data-test-id="agent-wizard-company-submit-button"
                        disabled={disabled}
                        label={COMPANY_COPY.cta}
                        style={{ width: 420 }}
                        type="submit"
                      />
                    </ButtonWrapper>
                    <Actions />
                  </>
                )}
              />
            )}
          </ContentContainer>
        </RightPanel>
      </Wrapper>
    </>
  );
};

export default AgentWizardCompany;
